import { Box, Button, Card, CardBody, Flex, Heading, HStack, Text, Textarea } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  MinimumRequirementResponseType,
  type MinimumRequirementDto,
  type SupplierBidV2MinimumRequirementResponseDto,
} from "../../../../autogen/bff-api";
import { StatusAccentBar } from "../../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import type { StatusVariant } from "../../../../common/components/StatusVariant";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";

// t('Your answer?') t('Yes') t('No') t('Explain your answer')
// t('Answered') t('Not answered') t('Comment required')

interface Props {
  requirement: MinimumRequirementDto;
  response: SupplierBidV2MinimumRequirementResponseDto | undefined;
  isReadOnly?: boolean;
  onAnswerChange: (answer: boolean) => void;
  onCommentChange: (comment: string) => void;
}

// Mirrors the BFF preflight completeness check.
export const isMinimumRequirementResponseComplete = (
  requirement: MinimumRequirementDto,
  response: SupplierBidV2MinimumRequirementResponseDto | undefined,
): boolean => {
  if (!response) return false;
  if (requirement.responseType === MinimumRequirementResponseType.YesNoComment && response.answer === false) {
    return (response.comment ?? "").trim().length > 0;
  }
  return true;
};

export const MinimumRequirementAnswerCard = ({
  requirement,
  response,
  isReadOnly,
  onAnswerChange,
  onCommentChange,
}: Props) => {
  const { t } = useTranslation();
  const variant: StatusVariant = !response
    ? "neutral"
    : isMinimumRequirementResponseComplete(requirement, response)
      ? "ok"
      : "issue";
  const statusLabel =
    variant === "ok" ? t("Answered") : variant === "issue" ? t("Comment required") : t("Not answered");
  const showComment =
    requirement.responseType === MinimumRequirementResponseType.YesNoComment && response?.answer === false;

  return (
    <Card variant="outline" rounded="xl" position="relative" overflow="hidden">
      <StatusAccentBar variant={variant} />
      <CardBody>
        <Heading size="sm" mb="1">
          {requirement.title}
        </Heading>
        {!isTiptapContentEmpty(requirement.description) && (
          <Box fontSize="sm" color="smMuted" mb="4">
            <TipTap content={requirement.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
          </Box>
        )}

        <Box borderWidth="1px" borderRadius="md" p="4">
          <Flex mb="2" justify="space-between" align="center" gap="2">
            <Text fontSize="sm" fontWeight="medium">
              {t("Your answer?")}
            </Text>
            <StatusDotLabel variant={variant} label={statusLabel} />
          </Flex>

          <HStack spacing="2">
            <Button
              size="sm"
              variant={response?.answer === true ? "solid" : "outline"}
              colorScheme="teal"
              isDisabled={isReadOnly}
              onClick={() => onAnswerChange(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              size="sm"
              variant={response?.answer === false ? "solid" : "outline"}
              colorScheme="teal"
              isDisabled={isReadOnly}
              onClick={() => onAnswerChange(false)}
            >
              {t("No")}
            </Button>
          </HStack>

          {showComment && (
            <Textarea
              mt="3"
              rows={3}
              placeholder={String(t("Explain your answer"))}
              isReadOnly={isReadOnly}
              value={response?.comment ?? ""}
              onChange={(e) => onCommentChange(e.target.value)}
            />
          )}
        </Box>
      </CardBody>
    </Card>
  );
};
